import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ConsultingCalendarInfo, ConsultingEvent, ProjectDto, TeamMemberDto } from '@jordan/types';
import { Alert, Breadcrumb, Button, Input, Modal, PageHeader, SelectMenu, Spinner } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { ApiError, apiDelete, apiGet, apiPatch, apiPost } from '../../lib/api';
import { billingTimeSidebar } from './billingTimeSidebar';

const TIMER_KEY = 'jordan.consultingTimer';
const HOUR_H = 48; // px per hour in the calendar grid
const DAY_MS = 86_400_000;

interface RunningTimer {
  label: string;
  startedAt: string; // ISO
}

// ---- date / format helpers (local time) ----
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
function mondayOf(d: Date): Date {
  const s = startOfDay(d);
  const dow = (s.getDay() + 6) % 7; // 0 = Monday
  s.setDate(s.getDate() - dow);
  return s;
}
const clock = (totalSec: number) => {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};
const timeLabel = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
const durationSec = (a: string, b: string) => (new Date(b).getTime() - new Date(a).getTime()) / 1000;
// Billed time (override) wins for display; flag when it differs from the appointment length.
const effectiveSec = (e: ConsultingEvent) => (e.billedMinutes != null ? e.billedMinutes * 60 : durationSec(e.start, e.end));
const isOverridden = (e: ConsultingEvent) => e.billedMinutes != null && e.billedMinutes !== Math.round(durationSec(e.start, e.end) / 60);

function dayHeading(day: Date): string {
  const today = startOfDay(new Date()).getTime();
  const t = startOfDay(day).getTime();
  if (t === today) return 'Today';
  if (t === today - DAY_MS) return 'Yesterday';
  return day.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function BillingTimeTimerPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [cal, setCal] = useState<ConsultingCalendarInfo | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [events, setEvents] = useState<ConsultingEvent[]>([]);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [view, setView] = useState<'calendar' | 'list'>('list');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [label, setLabel] = useState('');
  const [running, setRunning] = useState<RunningTimer | null>(() => {
    try {
      const raw = localStorage.getItem(TIMER_KEY);
      return raw ? (JSON.parse(raw) as RunningTimer) : null;
    } catch {
      return null;
    }
  });
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [menu, setMenu] = useState<{ event: ConsultingEvent; x: number; y: number } | null>(null);
  const [busy, setBusy] = useState(false);
  // Billing details modal (set-as-billable / edit) + the project & member pickers it needs.
  const [billingFor, setBillingFor] = useState<ConsultingEvent | null>(null);
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [team, setTeam] = useState<TeamMemberDto[]>([]);
  const gridRef = useRef<HTMLDivElement>(null);

  // Tick once a second while a timer is running.
  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [running]);

  const loadEvents = useCallback(() => {
    const from = weekStart.toISOString();
    const to = new Date(weekStart.getTime() + 7 * DAY_MS).toISOString();
    setLoading(true);
    setErr(null);
    apiGet<ConsultingEvent[]>(`/api/consulting/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`)
      .then((evs) => {
        setEvents(evs);
        setNotConfigured(false);
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 409) setNotConfigured(true);
        else setErr(e instanceof Error ? e.message : 'Could not load the calendar.');
      })
      .finally(() => setLoading(false));
  }, [weekStart]);

  useEffect(() => {
    apiGet<ConsultingCalendarInfo>('/api/consulting/calendar')
      .then(setCal)
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 409) setNotConfigured(true);
      });
    apiGet<ProjectDto[]>('/api/consulting/projects').then(setProjects).catch(() => undefined);
    apiGet<TeamMemberDto[]>('/api/consulting/team').then(setTeam).catch(() => undefined);
  }, []);
  useEffect(() => loadEvents(), [loadEvents]);

  // Scroll the calendar grid to ~7am on first render.
  useEffect(() => {
    if (view === 'calendar' && gridRef.current) gridRef.current.scrollTop = 7 * HOUR_H;
  }, [view]);

  // Close the context menu on any outside click / Escape.
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenu(null);
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [menu]);

  const startTimer = () => {
    const t = { label: label.trim(), startedAt: new Date().toISOString() };
    localStorage.setItem(TIMER_KEY, JSON.stringify(t));
    setRunning(t);
    setNowMs(Date.now());
  };
  const stopTimer = async () => {
    if (!running) return;
    setBusy(true);
    setErr(null);
    try {
      await apiPost('/api/consulting/events', {
        summary: running.label || '(no description)',
        start: running.startedAt,
        end: new Date().toISOString(),
      });
      localStorage.removeItem(TIMER_KEY);
      setRunning(null);
      setLabel('');
      loadEvents();
    } catch {
      setErr('Could not save the time entry to the calendar.');
    } finally {
      setBusy(false);
    }
  };

  const openMenu = (e: React.MouseEvent, event: ConsultingEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenu({ event, x: e.clientX, y: e.clientY });
  };
  const openBilling = (ev: ConsultingEvent) => {
    setMenu(null);
    setBillingFor(ev);
  };
  const setNonBillable = async (ev: ConsultingEvent) => {
    setMenu(null);
    try {
      await apiPatch(`/api/consulting/events/${ev.id}`, { billable: false });
      setEvents((list) => list.map((x) => (x.id === ev.id ? { ...x, billable: false, projectId: null, memberId: null, amount: null } : x)));
    } catch {
      setErr('Could not update the entry.');
    }
  };
  const removeEvent = async (ev: ConsultingEvent) => {
    setMenu(null);
    try {
      await apiDelete(`/api/consulting/events/${ev.id}`);
      setEvents((list) => list.filter((x) => x.id !== ev.id));
    } catch {
      setErr('Could not delete the entry.');
    }
  };
  const copyDescription = (ev: ConsultingEvent) => {
    setMenu(null);
    void navigator.clipboard?.writeText(ev.summary);
  };

  const weekDays = Array.from({ length: 7 }, (_, i) => new Date(weekStart.getTime() + i * DAY_MS));
  const weekLabel = `${weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${weekDays[6].toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  const shiftWeek = (deltaWeeks: number) => setWeekStart((w) => new Date(w.getTime() + deltaWeeks * 7 * DAY_MS));

  const elapsed = running ? (nowMs - new Date(running.startedAt).getTime()) / 1000 : 0;

  return (
    <AppChrome secondarySidebar={billingTimeSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Billing &amp; Time - <span className="fw-normal">Timer</span>
          </>
        }
        breadcrumb={
          <Breadcrumb
            items={[
              { icon: 'ph-house', href: '/' },
              { label: 'Consulting' },
              { label: 'Billing & Time' },
              { label: 'Timer' },
            ]}
          />
        }
      />

      <div className="content">
        {err && (
          <Alert variant="warning" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {notConfigured ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="ph-calendar-x d-block fs-1 text-muted mb-2" />
              <h5 className="mb-1">No calendar configured</h5>
              <p className="text-muted mb-3">Pick the calendar this app should track time against.</p>
              <Button variant="primary" icon="ph-gear" onClick={() => navigate('/configuration/consulting')}>
                Configure calendar
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Timer bar */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body d-flex flex-wrap align-items-center gap-2">
                <div className="flex-grow-1" style={{ minWidth: 240 }}>
                  <Input
                    className="mb-0"
                    placeholder="What are you working on?"
                    value={running ? running.label : label}
                    disabled={!!running}
                    onChange={(e) => setLabel(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !running && startTimer()}
                  />
                </div>
                <span className="font-monospace fs-5 mx-2" style={{ minWidth: 92, textAlign: 'right' }}>
                  {clock(elapsed)}
                </span>
                {running ? (
                  <Button variant="danger" icon="ph-stop-circle" disabled={busy} onClick={() => void stopTimer()}>
                    Stop
                  </Button>
                ) : (
                  <Button variant="primary" icon="ph-play-circle" onClick={startTimer}>
                    Start
                  </Button>
                )}
              </div>
            </div>

            {/* Toolbar: week nav + view toggle */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              <Button variant="light" icon="ph-caret-left" onClick={() => shiftWeek(-1)} />
              <Button variant="light" onClick={() => setWeekStart(mondayOf(new Date()))}>
                Today
              </Button>
              <Button variant="light" icon="ph-caret-right" onClick={() => shiftWeek(1)} />
              <span className="fw-semibold ms-2">{weekLabel}</span>
              {cal && <span className="text-muted fs-sm">· {cal.summary}</span>}
              <div className="btn-group ms-auto">
                <button
                  className={`btn btn-sm ${view === 'calendar' ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => setView('calendar')}
                >
                  <i className="ph-calendar-blank me-1" /> Calendar
                </button>
                <button
                  className={`btn btn-sm ${view === 'list' ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => setView('list')}
                >
                  <i className="ph-list me-1" /> List
                </button>
              </div>
            </div>

            {loading && events.length === 0 ? (
              <Spinner />
            ) : view === 'calendar' ? (
              <CalendarGrid
                weekDays={weekDays}
                events={events}
                gridRef={gridRef}
                onEventClick={openMenu}
              />
            ) : (
              <ListView weekDays={weekDays} events={events} projects={projects} onMenu={openMenu} />
            )}
          </>
        )}
      </div>

      {menu && (
        <EntryMenu
          menu={menu}
          onBilling={() => openBilling(menu.event)}
          onSetNonBillable={() => void setNonBillable(menu.event)}
          onCopy={() => copyDescription(menu.event)}
          onDelete={() => void removeEvent(menu.event)}
        />
      )}

      {billingFor && (
        <BillingModal
          event={billingFor}
          projects={projects}
          team={team}
          onClose={() => setBillingFor(null)}
          onSaved={(updated) => {
            setEvents((list) => list.map((x) => (x.id === updated.id ? updated : x)));
            setBillingFor(null);
          }}
          onError={() => setErr('Could not save the billing details.')}
        />
      )}
    </AppChrome>
  );
}

// ---- Calendar (week) grid ----
function CalendarGrid({
  weekDays,
  events,
  gridRef,
  onEventClick,
}: {
  weekDays: Date[];
  events: ConsultingEvent[];
  gridRef: React.RefObject<HTMLDivElement>;
  onEventClick: (e: React.MouseEvent, ev: ConsultingEvent) => void;
}) {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  return (
    <div className="card border-0 shadow-sm">
      {/* Day headers */}
      <div className="d-flex border-bottom">
        <div style={{ width: 56, flex: '0 0 56px' }} />
        {weekDays.map((d) => (
          <div key={d.toISOString()} className="flex-fill text-center py-2 border-start">
            <div className="fw-bold">{d.getDate()}</div>
            <div className="text-muted fs-sm text-uppercase">{d.toLocaleDateString(undefined, { weekday: 'short' })}</div>
          </div>
        ))}
      </div>
      {/* Scrollable time grid */}
      <div ref={gridRef} style={{ overflowY: 'auto', maxHeight: 600 }}>
        <div className="d-flex" style={{ position: 'relative', height: 24 * HOUR_H }}>
          {/* Hour gutter */}
          <div style={{ width: 56, flex: '0 0 56px', position: 'relative' }}>
            {hours.map((h) => (
              <div
                key={h}
                className="text-muted fs-sm"
                style={{ position: 'absolute', top: h * HOUR_H - 7, right: 6 }}
              >
                {h === 0 ? '' : `${((h + 11) % 12) + 1} ${h < 12 ? 'AM' : 'PM'}`}
              </div>
            ))}
          </div>
          {/* Day columns */}
          {weekDays.map((day) => {
            const dayStart = startOfDay(day).getTime();
            const dayEnd = dayStart + DAY_MS;
            const dayEvents = events.filter(
              (e) => !e.allDay && new Date(e.start).getTime() < dayEnd && new Date(e.end).getTime() > dayStart,
            );
            return (
              <div key={day.toISOString()} className="flex-fill border-start" style={{ position: 'relative' }}>
                {hours.map((h) => (
                  <div
                    key={h}
                    style={{ position: 'absolute', top: h * HOUR_H, left: 0, right: 0, borderTop: '1px solid var(--gray-200)' }}
                  />
                ))}
                {dayEvents.map((ev) => {
                  const s = Math.max(dayStart, new Date(ev.start).getTime());
                  const e = Math.min(dayEnd, new Date(ev.end).getTime());
                  const top = ((s - dayStart) / DAY_MS) * 24 * HOUR_H;
                  const height = Math.max(16, ((e - s) / DAY_MS) * 24 * HOUR_H);
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      onClick={(me) => onEventClick(me, ev)}
                      title={ev.summary}
                      style={{
                        position: 'absolute',
                        top,
                        height,
                        left: 2,
                        right: 2,
                        overflow: 'hidden',
                        textAlign: 'left',
                        padding: '2px 6px',
                        fontSize: 12,
                        lineHeight: 1.2,
                        borderRadius: 4,
                        border: 'none',
                        color: 'var(--white)',
                        background: ev.billable ? 'var(--bs-primary)' : 'var(--gray-500)',
                      }}
                    >
                      {ev.billable && <i className="ph-currency-dollar me-1" />}
                      {ev.summary}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---- List view ----
function ListView({
  weekDays,
  events,
  projects,
  onMenu,
}: {
  weekDays: Date[];
  events: ConsultingEvent[];
  projects: ProjectDto[];
  onMenu: (e: React.MouseEvent, ev: ConsultingEvent) => void;
}) {
  const colorByProject = new Map(projects.map((p) => [p.id, p.color]));
  // Group this week's events by local day, most recent first.
  const groups = weekDays
    .map((day) => {
      const dayStart = startOfDay(day).getTime();
      const items = events
        .filter((e) => !e.allDay && startOfDay(new Date(e.start)).getTime() === dayStart)
        .sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime());
      const total = items.reduce((sum, e) => sum + effectiveSec(e), 0);
      return { day, items, total };
    })
    .filter((g) => g.items.length > 0)
    .reverse();

  if (groups.length === 0) {
    return (
      <div className="card border-0 shadow-sm">
        <div className="card-body text-center text-muted py-5">No time entries this week.</div>
      </div>
    );
  }

  return (
    <div className="card border-0 shadow-sm">
      <div className="list-group list-group-flush">
        {groups.map(({ day, items, total }) => (
          <div key={day.toISOString()}>
            <div className="list-group-item d-flex align-items-center bg-light fw-semibold">
              <span>{dayHeading(day)}</span>
              <span className="ms-auto font-monospace">{clock(total)}</span>
            </div>
            {items.map((ev) => (
              <div key={ev.id} className="list-group-item d-flex align-items-center">
                <span className="fw-semibold text-truncate" style={{ maxWidth: '40%' }}>
                  {ev.summary}
                </span>
                {ev.billable &&
                  (() => {
                    const color = ev.projectId ? colorByProject.get(ev.projectId) : undefined;
                    return color ? (
                      <span className="badge ms-2" style={{ color, background: `${color}22` }} title="Billable">
                        <i className="ph-currency-dollar" />
                      </span>
                    ) : (
                      <span className="badge bg-primary bg-opacity-25 text-primary ms-2" title="Billable">
                        <i className="ph-currency-dollar" />
                      </span>
                    );
                  })()}
                <span className="ms-auto text-muted me-3">
                  {timeLabel(ev.start)} – {timeLabel(ev.end)}
                </span>
                <span
                  className={`font-monospace me-2 ${isOverridden(ev) ? 'fst-italic' : ''}`.trim()}
                  style={{ minWidth: 72, textAlign: 'right' }}
                  title={isOverridden(ev) ? `Edited from the ${clock(durationSec(ev.start, ev.end))} appointment` : undefined}
                >
                  {clock(effectiveSec(ev))}
                </span>
                <button
                  type="button"
                  className="btn btn-light btn-icon btn-sm rounded-pill border-transparent"
                  onClick={(e) => onMenu(e, ev)}
                  title="Actions"
                >
                  <i className="ph-dots-three-vertical" />
                </button>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Entry context menu (fixed position at the click point) ----
function EntryMenu({
  menu,
  onBilling,
  onSetNonBillable,
  onCopy,
  onDelete,
}: {
  menu: { event: ConsultingEvent; x: number; y: number };
  onBilling: () => void;
  onSetNonBillable: () => void;
  onCopy: () => void;
  onDelete: () => void;
}) {
  // Keep the menu on-screen near the click.
  const left = Math.min(menu.x, window.innerWidth - 220);
  const top = Math.min(menu.y, window.innerHeight - 220);
  const billable = menu.event.billable;
  return (
    <div
      className="dropdown-menu show shadow"
      style={{ position: 'fixed', left, top, zIndex: 2000, minWidth: 200 }}
      onClick={(e) => e.stopPropagation()}
    >
      {billable ? (
        <>
          <button className="dropdown-item" onClick={onBilling}>
            <i className="ph-pencil me-2" /> Edit billing
          </button>
          <button className="dropdown-item" onClick={onSetNonBillable}>
            <i className="ph-prohibit me-2" /> Set as non-billable
          </button>
        </>
      ) : (
        <button className="dropdown-item" onClick={onBilling}>
          <i className="ph-currency-dollar me-2" /> Set as billable
        </button>
      )}
      <button className="dropdown-item" onClick={onCopy}>
        <i className="ph-copy me-2" /> Copy description
      </button>
      <div className="dropdown-divider" />
      <button className="dropdown-item text-danger" onClick={onDelete}>
        <i className="ph-trash me-2" /> Delete
      </button>
    </div>
  );
}

// ---- Billing details modal (project, member, amount) ----
function BillingModal({
  event,
  projects,
  team,
  onClose,
  onSaved,
  onError,
}: {
  event: ConsultingEvent;
  projects: ProjectDto[];
  team: TeamMemberDto[];
  onClose: () => void;
  onSaved: (e: ConsultingEvent) => void;
  onError: () => void;
}) {
  const [projectId, setProjectId] = useState(event.projectId ?? '');
  const [memberId, setMemberId] = useState(event.memberId ?? '');
  const [amount, setAmount] = useState(event.amount == null ? '' : String(event.amount));
  // Time spent (minutes): defaults to the appointment's duration; the set value wins for billing.
  const calMinutes = Math.max(0, Math.round((new Date(event.end).getTime() - new Date(event.start).getTime()) / 60000));
  const [minutes, setMinutes] = useState(String(event.billedMinutes ?? calMinutes));
  const [busy, setBusy] = useState(false);

  // Suggest the member's current rate as the default amount when one is picked and none is set.
  const pickMember = (id: string) => {
    setMemberId(id);
    if (!amount) {
      const m = team.find((x) => x.id === id);
      if (m?.rate) setAmount(String(m.rate.amount));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const billedMinutes = minutes.trim() === '' ? null : Math.round(Number(minutes));
      await apiPatch(`/api/consulting/events/${event.id}`, {
        billable: true,
        projectId: projectId || null,
        memberId: memberId || null,
        amount: amount.trim() === '' ? null : Number(amount),
        billedMinutes,
      });
      onSaved({
        ...event,
        billable: true,
        projectId: projectId || null,
        memberId: memberId || null,
        amount: amount.trim() === '' ? null : Number(amount),
        billedMinutes,
      });
    } catch {
      onError();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      centered
      onClose={onClose}
      title={event.billable ? 'Edit billing' : 'Set as billable'}
      footer={
        <>
          <Button variant="light" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void save()}>
            Save
          </Button>
        </>
      }
    >
      <div className="text-muted mb-3">{event.summary}</div>
      <SelectMenu
        label="Project"
        icon="ph-folders"
        value={projectId}
        onChange={setProjectId}
        options={[{ value: '', label: 'No project' }, ...projects.map((p) => ({ value: p.id, label: p.name }))]}
      />
      <SelectMenu
        label="Team member"
        icon="ph-user"
        value={memberId}
        onChange={pickMember}
        options={[{ value: '', label: 'No member' }, ...team.map((m) => ({ value: m.id, label: m.name }))]}
      />
      <Input
        label="Billing amount"
        type="number"
        min={0}
        step="0.01"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        hint="Defaults to the selected member’s rate; change it for this entry."
      />
      <Input
        label="Time spent (minutes)"
        type="number"
        min={0}
        step={1}
        value={minutes}
        onChange={(e) => setMinutes(e.target.value)}
        hint={`Defaults to the appointment length (${calMinutes} min). This billed time is the source of truth.`}
      />
    </Modal>
  );
}
